import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Paper,
  Button,
  Box,
  TextField,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { ROUTES } from "../../constants";

const PendingWaterServiceConnectionsList = ({ connections }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const filteredConnections = useMemo(() => {
    if (!searchTerm) return connections;
    const term = searchTerm.toLowerCase();
    return connections.filter((conn) => {
      return (
        `${conn.pws_connection_ndx || ""}`.toLowerCase().includes(term) ||
        `${conn.first_name || ""} ${conn.last_name || ""}`
          .toLowerCase()
          .includes(term) ||
        `${conn.company_name || ""}`.toLowerCase().includes(term) ||
        `${conn.street_city || ""}`.toLowerCase().includes(term) ||
        `${conn.mail_city || ""}`.toLowerCase().includes(term)
      );
    });
  }, [connections, searchTerm]);

  return (
    <Box sx={{ width: "100%", px: 2, py: 3 }}>
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          maxWidth: "1000px",
          margin: "0 auto",
          borderBottom: "1px solid rgba(0, 0, 0, 0.5)",
          pb: 4,
          mb: 4,
        }}
      >
        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                border: "2px solid",
                borderColor: "secondary.main",
                borderRadius: 2,
                px: 2,
                py: 1,
                minWidth: 64,
                textAlign: "center",
              }}
            >
              <Typography variant="h4" color="secondary" fontWeight={700}>
                {connections.length}
              </Typography>
            </Box>
            <Typography variant="h6">Pending Connections</Typography>
          </Box>
        </Grid>

        <Grid item xs>
          <TextField
            placeholder="Search by name, company, or city"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
      </Grid>

      {(!filteredConnections || filteredConnections.length === 0) && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 4 }}
        >
          No pending connections found.
        </Typography>
      )}

      <Grid container spacing={3} justifyContent="center">
        {filteredConnections.map((conn) => {
          const editUrl = `${ROUTES.PENDING_WATER_SERVICE_CONNECTIONS_MANAGEMENT}/${conn.pws_connection_ndx}`;
          return (
            <Grid item xs={12} md={10} lg={8} key={conn.pws_connection_ndx}>
              <Paper
                variant="outlined"
                sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    #{conn.pws_connection_ndx}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {[conn.first_name, conn.last_name]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </Typography>
                  {conn.company_name && (
                    <Typography variant="body2" color="text.secondary">
                      {conn.company_name}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Mailing Contact:
                  </Typography>
                  {conn.mail_address && (
                    <Typography variant="body2" color="text.secondary">
                      {conn.mail_address}
                    </Typography>
                  )}
                  {[conn.mail_city, conn.mail_state, conn.mail_zip].some(
                    Boolean
                  ) && (
                    <Typography variant="body2" color="text.secondary">
                      {[conn.mail_city, conn.mail_state, conn.mail_zip]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  )}
                  {conn.mail_phone && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {conn.mail_phone}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Service Location:
                  </Typography>
                  {conn.street_address && (
                    <Typography variant="body2" color="text.secondary">
                      {conn.street_address}
                    </Typography>
                  )}
                  {[conn.street_city, conn.street_state, conn.street_zip].some(
                    Boolean
                  ) && (
                    <Typography variant="body2" color="text.secondary">
                      {[conn.street_city, conn.street_state, conn.street_zip]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1 }} />

                {conn.date_received && (
                  <Typography variant="body2" color="text.secondary">
                    Received: {conn.date_received.substring(0, 10)}
                  </Typography>
                )}

                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  {isXs ? (
                    <IconButton component={Link} to={editUrl} color="secondary">
                      <EditIcon />
                    </IconButton>
                  ) : (
                    <Button
                      component={Link}
                      to={editUrl}
                      variant="outlined"
                      color="secondary"
                      startIcon={<EditIcon />}
                    >
                      Review
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PendingWaterServiceConnectionsList;
